import { useState, useEffect, useCallback, useRef } from 'react';
import { getTasks, createTask, updateTask, deleteTask } from '../services/api';
import { supabase } from '../config/supabase';
import type { Task, CreateTaskInput, UpdateTaskInput } from '../types';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface UseTasksOptions {
  autoRefetch?: boolean;
  enableRealtime?: boolean;
}

export const useTasksWithRealtime = (options: UseTasksOptions = {}) => {
  const { autoRefetch = false, enableRealtime = true } = options;
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const cacheRef = useRef<{ tasks: Task[]; timestamp: number } | null>(null);
  const CACHE_DURATION = 30000; // 30 seconds

  const fetchTasks = useCallback(async (filters?: Record<string, any>) => {
    try {
      // Check cache first
      const now = Date.now();
      if (cacheRef.current && (now - cacheRef.current.timestamp < CACHE_DURATION)) {
        console.log('[useTasks] Using cached tasks');
        setTasks(cacheRef.current.tasks);
        setLoading(false);
        return cacheRef.current.tasks;
      }

      setLoading(true);
      setError(null);
      console.log('[useTasks] Fetching tasks from API');
      const data = await getTasks(filters || { is_archived: false });
      
      // Update cache
      cacheRef.current = { tasks: data, timestamp: now };
      setTasks(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tasks';
      setError(message);
      console.error('[useTasks] Error fetching tasks:', message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const addTask = useCallback(async (taskInput: CreateTaskInput) => {
    try {
      const newTask = await createTask(taskInput);
      
      // Optimistic update
      setTasks((prev) => [newTask, ...prev]);
      
      // Update cache
      if (cacheRef.current) {
        cacheRef.current.tasks = [newTask, ...cacheRef.current.tasks];
        cacheRef.current.timestamp = Date.now();
      }
      
      return newTask;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create task';
      setError(message);
      throw err;
    }
  }, []);

  const editTask = useCallback(async (id: string, taskInput: UpdateTaskInput) => {
    try {
      // Optimistic update
      setTasks((prev) => prev.map((task) => 
        task.id === id ? { ...task, ...taskInput } as Task : task
      ));

      const updatedTask = await updateTask(id, taskInput);
      
      // Real update
      setTasks((prev) => prev.map((task) => (task.id === id ? updatedTask : task)));
      
      // Update cache
      if (cacheRef.current) {
        cacheRef.current.tasks = cacheRef.current.tasks.map((task) =>
          task.id === id ? updatedTask : task
        );
        cacheRef.current.timestamp = Date.now();
      }
      
      return updatedTask;
    } catch (err) {
      // Revert optimistic update on error
      await fetchTasks();
      const message = err instanceof Error ? err.message : 'Failed to update task';
      setError(message);
      throw err;
    }
  }, [fetchTasks]);

  const removeTask = useCallback(async (id: string) => {
    try {
      // Optimistic update
      setTasks((prev) => prev.filter((task) => task.id !== id));

      await deleteTask(id);
      
      // Update cache
      if (cacheRef.current) {
        cacheRef.current.tasks = cacheRef.current.tasks.filter((task) => task.id !== id);
        cacheRef.current.timestamp = Date.now();
      }
    } catch (err) {
      // Revert optimistic update on error
      await fetchTasks();
      const message = err instanceof Error ? err.message : 'Failed to delete task';
      setError(message);
      throw err;
    }
  }, [fetchTasks]);

  const invalidateCache = useCallback(() => {
    cacheRef.current = null;
  }, []);

  // Setup Supabase realtime subscription
  useEffect(() => {
    if (!enableRealtime) return;

    const channel = supabase
      .channel('tasks_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
        },
        (payload) => {
          console.log('[useTasks] Realtime event received:', payload.eventType);

          if (payload.eventType === 'INSERT') {
            const newTask = payload.new as Task;
            setTasks((prev) => {
              // Avoid duplicates
              if (prev.some(t => t.id === newTask.id)) return prev;
              return [newTask, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedTask = payload.new as Task;
            setTasks((prev) => prev.map((task) => 
              task.id === updatedTask.id ? updatedTask : task
            ));
          } else if (payload.eventType === 'DELETE') {
            const deletedTask = payload.old as Task;
            setTasks((prev) => prev.filter((task) => task.id !== deletedTask.id));
          }

          // Invalidate cache on any change
          invalidateCache();
        }
      )
      .subscribe((status) => {
        console.log('[useTasks] Realtime subscription status:', status);
      });

    channelRef.current = channel;

    return () => {
      console.log('[useTasks] Cleaning up realtime subscription');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [enableRealtime, invalidateCache]);

  // Initial fetch
  useEffect(() => {
    fetchTasks();

    // Listen for task changes from chatbot
    const handleTaskChanged = () => {
      console.log('[useTasks] Task changed event received from chatbot, refreshing...');
      invalidateCache();
      fetchTasks();
    };

    window.addEventListener('taskChanged', handleTaskChanged);

    return () => {
      window.removeEventListener('taskChanged', handleTaskChanged);
    };
  }, [fetchTasks, invalidateCache]);

  // Auto-refetch interval (optional)
  useEffect(() => {
    if (!autoRefetch) return;

    const interval = setInterval(() => {
      console.log('[useTasks] Auto-refetching tasks');
      invalidateCache();
      fetchTasks();
    }, 60000); // Every 60 seconds

    return () => clearInterval(interval);
  }, [autoRefetch, fetchTasks, invalidateCache]);

  return {
    tasks,
    loading,
    error,
    fetchTasks,
    addTask,
    editTask,
    removeTask,
    invalidateCache,
  };
};
